// Helpers for the add-account popup (single form and CSV upload).
// Pure functions: no API calls, no state. The same cleaning rules the backend
// uses for contacts are applied here, so the preview matches what gets saved.

export const ACCOUNT_CSV_FIELDS = [
  { key: "domain", aliases: ["domain", "website domain", "company domain"] },
  { key: "name", aliases: ["name", "company name", "account name"] },
  { key: "industry", aliases: ["industry"] },
  { key: "employeeCount", aliases: ["employee_count", "employee count", "employees", "no of employees"] },
  { key: "revenue", aliases: ["revenue"] },
  { key: "linkedinId", aliases: ["linkedin_url", "linkedin url", "linkedin"] },
  { key: "secondaryWebsites", aliases: ["website_alias", "website alias", "secondary websites", "aliases"] },
  { key: "phone", aliases: ["phone", "phone number"] },
  { key: "otherPhone", aliases: ["other_phone", "other phone", "other number"] }
];

const MAX_CUSTOM_FIELDS = 30;

const normalise = text => (text || "").toString().trim().toLowerCase();

// Splits CSV text into headers and rows. Handles quoted cells and "" escapes.
export const parseCsvText = text => {
  const source = (text || "").replace(/^﻿/, "");
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < source.length; i++) {
    const ch = source[i];
    if (inQuotes) {
      if (ch === '"') {
        if (source[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && source[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += ch;
    }
  }
  if (cell.length || row.length) {
    row.push(cell);
    rows.push(row);
  }
  const filled = rows.filter(r => r.some(c => c.trim() !== ""));
  if (!filled.length) {
    return { headers: [], rows: [] };
  }
  const [headers, ...body] = filled;
  return { headers: headers.map(h => h.trim()), rows: body };
};

// Maps each CSV header to an account field key, or null when it isn't recognised.
// Custom fields map by their team label, or by "Custom Field N" for enabled slots.
export const mapHeaders = (headers = [], customLabels = {}) => {
  const labelToSlot = {};
  Object.keys(customLabels || {}).forEach(key => {
    if (/^customField\d+$/.test(key) && customLabels[key]) {
      labelToSlot[normalise(customLabels[key])] = key;
    }
  });
  const used = new Set();
  return headers.map(header => {
    const h = normalise(header);
    let key = null;
    const field = ACCOUNT_CSV_FIELDS.find(f => f.aliases.includes(h));
    if (field) {
      key = field.key;
    } else if (labelToSlot[h]) {
      key = labelToSlot[h];
    } else {
      const match = h.match(/^custom\s*field\s*(\d+)$/);
      if (match && +match[1] >= 1 && +match[1] <= MAX_CUSTOM_FIELDS) {
        const slot = `customField${+match[1]}`;
        if (customLabels?.[slot]) key = slot;
      }
    }
    if (key && used.has(key)) {
      return null;
    }
    if (key) used.add(key);
    return key;
  });
};

// Same rule as the contact LinkedIn cleaning: no query string, protocol, www or trailing slash.
export const cleanLinkedinUrl = value => {
  let url = (value || "").trim();
  if (!url) return null;
  url = url.split("?")[0];
  url = url.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
  url = url.replace(/\/$/, "");
  return url || null;
};

// Domain or alias: lowercase, no protocol, www or path.
export const cleanDomain = value => {
  let domain = (value || "").trim().toLowerCase();
  domain = domain.replace(/^https?:\/\//, "").replace(/^www\./, "");
  domain = domain.split("/")[0];
  return domain || "";
};

// Several values in one cell, separated by ; , or |.
export const splitMulti = value =>
  (value || "")
    .split(/[;,|]/)
    .map(v => v.trim())
    .filter(Boolean);

// Builds the account object the create API expects from field values.
export const valuesToAccount = (values = {}) => {
  const account = { domain: cleanDomain(values.domain) };
  const name = (values.name || "").trim();
  if (name) account.name = name;
  ["industry", "employeeCount", "revenue"].forEach(key => {
    if ((values[key] || "").trim()) account[key] = values[key].trim();
  });
  const linkedinId = cleanLinkedinUrl(values.linkedinId);
  if (linkedinId) account.linkedinId = linkedinId;
  const aliases = splitMulti(values.secondaryWebsites).map(cleanDomain).filter(Boolean);
  if (aliases.length) account.secondaryWebsites = aliases;
  const phone = splitMulti(values.phone);
  if (phone.length) account.phone = phone;
  const otherPhone = splitMulti(values.otherPhone);
  if (otherPhone.length) account.otherPhone = otherPhone;
  const customFields = {};
  Object.keys(values).forEach(key => {
    if (/^customField\d+$/.test(key) && (values[key] || "").trim()) {
      customFields[key] = values[key].trim();
    }
  });
  if (Object.keys(customFields).length) account.customFields = customFields;
  return account;
};

// One CSV row becomes field values (using the header mapping), then an account.
export const rowToAccount = (cells = [], mapping = []) => {
  const values = {};
  mapping.forEach((key, index) => {
    if (key) values[key] = cells[index] || "";
  });
  return valuesToAccount(values);
};
